import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Grid,
  IconButton,
  InputBase,
  Paper,
  Autocomplete,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { PlaceOutlined } from "@mui/icons-material";
import { fonts, inputBaseStyles, noOutline } from "../../Styles/Styles";

const SearchBar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const queryFromURL = searchParams.get("query") || "";
  const locationFromURL = searchParams.get("location") || "";

  const [searchTerm, setSearchTerm] = useState(queryFromURL);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(locationFromURL);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    setSearchTerm(queryFromURL);
    setSelectedLocation(locationFromURL);
  }, [queryFromURL, locationFromURL]);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_BASE_URL}/locations/`
        );
        if (response.ok) {
          const data = await response.json();
          setLocations(data);
        } else {
          console.error("Failed to fetch locations");
        }
      } catch (error) {
        console.error("Error fetching locations:", error);
      }
    };
    fetchLocations();
  }, []);

  const handleSearch = (searchTerm, location) => {
    const currentParams = new URLSearchParams(window.location.search);

    if (searchTerm) {
      currentParams.set("query", searchTerm);
    } else {
      currentParams.delete("query");
    }

    if (location) {
      currentParams.set("location", location);
    } else {
      currentParams.delete("location");
    }

    navigate(`/ads?${currentParams.toString()}`);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!searchTerm.trim() && !selectedLocation) {
      setSnackbarOpen(true);
      return;
    }
    handleSearch(searchTerm, selectedLocation);
  };

  const handleLocationSelect = (event, newValue) => {
    setSelectedLocation(newValue ? newValue.name : "");
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Grid
      container
      // spacing={2} // Add spacing between items
      justifyContent="center"
      sx={{ width: "100%", maxWidth: "800px", margin: "0 auto" }}
    >
      <Grid item xs={12}>
        <Paper
          variant="outlined"
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: { xs: "wrap", sm: "nowrap" },
            width: "100%",
            boxShadow: "0 4px 15px rgba(0, 0, 0, 0.15)",
            padding: "15px 15px",
            // borderRadius: "50px",
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            transition: "box-shadow 0.3s ease-in-out",
            "&:hover": { boxShadow: "0 8px 25px rgba(0, 0, 0, 0.2)" },
          }}
        >
          {/* Location Field */}
          <Grid
            item
            xs={12}
            sm="auto"
            sx={{ mx: 1, my: { sm: 1, xs: 1, md: 0 } }}
          >
            <Autocomplete
              options={locations}
              getOptionLabel={(option) => option.name} // Return only the string
              renderOption={(props, option) => (
                <li {...props}>
                  <Typography sx={{ ...fonts }}>{option.name}</Typography>
                </li>
              )}
              value={
                locations.find((loc) => loc.name === selectedLocation) || null
              }
              onChange={handleLocationSelect}
              sx={{
                width: { xs: "100%", sm: "250px" },
                "& .MuiOutlinedInput-root": {
                  fontFamily: "inherit",
                  fontWeight: "Medium",
                  padding: "6px 15px",
                  borderRadius: "50px",
                  backgroundColor: "white",
                  border: "1px solid rgba(0, 0, 0, 0.2)",
                  "&:hover": { borderColor: "var(--primary-color)" },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Location"
                  variant="outlined"
                  sx={{
                    borderRadius: "50px",
                    "& fieldset": { border: "none" },
                    fontFamily: "inherit",
                    fontWeight: "Regular",
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <PlaceOutlined
                          sx={{
                            color: "var(--primary-color)",
                            marginRight: "8px",
                          }}
                        />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          {/* Search Input */}
          <Grid item xs={12} sm sx={{ mx: 1, flex: 1 }}>
            <InputBase
              sx={{ ...inputBaseStyles }}
              startAdornment={
                <SearchIcon
                  sx={{ color: "var(--primary-color)", marginRight: "8px" }}
                />
              }
              placeholder="I'm looking for..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Grid>

          {/* Search Button */}
          <Grid item sx={{ mx: 1 }}>
            <IconButton
              type="submit"
              sx={{
                p: "12px",
                borderRadius: "50px",
                color: "white",
                backgroundColor: "var(--primary-color)",
                transition: "background-color 0.3s",
                "&:hover": { backgroundColor: "var(--primary-color)" },
                ...noOutline,
              }}
              aria-label="search"
            >
              <SearchIcon />
            </IconButton>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default SearchBar;
